/** CloudFront 배포 도메인. 앱의 .env(VITE_CDN_BASE_URL)에서 주입 */
export const CDN_BASE_URL = import.meta.env.VITE_CDN_BASE_URL;

/**
 * 업로드 용도. 백엔드 FilePurpose enum과 값이 일치해야 한다.
 * (FE는 erasableSyntaxOnly 기조라 enum 대신 const 객체 사용)
 */
export const FilePurpose = {
  AVATAR: 'AVATAR',
  POST: 'POST',
  DOCUMENT: 'DOCUMENT',
  OTHER: 'OTHER',
  /** 도움말(/docs) 본문 미디어 — 캡처와 무음 스크린캐스트. video 를 받는 유일한 purpose(ADMIN 전용). */
  DOCS_MEDIA: 'DOCS_MEDIA',
  /**
   * 품셈해설(/pumsem) 본문 도해 — **이미지만**, ADMIN 전용.
   * DOCS_MEDIA 와 나눈 이유는 S3 prefix 다(purpose 가 곧 보관 위치라 섞이면
   * 나중에 갈라낼 수 없다).
   */
  PUMSEM_MEDIA: 'PUMSEM_MEDIA',
  PAGE_HEADER: 'PAGE_HEADER',
  PAGE_HOME: 'PAGE_HOME',
  PAGE_STORE: 'PAGE_STORE',
  ORG_CERTIFICATE: 'ORG_CERTIFICATE',
} as const;

export type FilePurpose = (typeof FilePurpose)[keyof typeof FilePurpose];

/** 백엔드 ALLOWED_MIME_TYPES와 동기화 */
export const ALLOWED_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;

export type AllowedImageMimeType = (typeof ALLOWED_IMAGE_MIME_TYPES)[number];

export function isAllowedImageType(mime: string): mime is AllowedImageMimeType {
  return (ALLOWED_IMAGE_MIME_TYPES as readonly string[]).includes(mime);
}

/**
 * 무음 스크린캐스트 허용 MIME. 백엔드 VIDEO_MIME_TYPES 와 동기화.
 *
 * 서버는 이 목록을 **DOCS_MEDIA purpose 에서만** 받는다. 다른 purpose 로 올리면
 * 거절된다 — 영상 업로드는 도움말 전용 통로다.
 * GIF 는 일부러 뺐다(같은 클립이 20배 크고 256색이라 UI 글자가 뭉개진다).
 */
export const ALLOWED_VIDEO_MIME_TYPES = ['video/mp4', 'video/webm'] as const;

export type AllowedVideoMimeType = (typeof ALLOWED_VIDEO_MIME_TYPES)[number];

export function isAllowedVideoType(mime: string): mime is AllowedVideoMimeType {
  return (ALLOWED_VIDEO_MIME_TYPES as readonly string[]).includes(mime);
}

/** 도움말 본문 미디어(캡처 + 영상). */
export function isAllowedDocsMediaType(mime: string): boolean {
  return isAllowedImageType(mime) || isAllowedVideoType(mime);
}

/**
 * 첨부 문서 확장자별 허용 MIME. 백엔드 media.constant.ts DOCUMENT_MIME_BY_EXT 와 동기화.
 * 같은 확장자를 브라우저·OS 마다 다른 MIME 으로 보내기도 해서(한글, Windows 의 zip) 값이 배열이다.
 * 게시판 첨부(pdf/office/한글/압축/텍스트 등)에서 사용.
 */
const DOCUMENT_MIME_BY_EXT = {
  pdf: ['application/pdf'],
  doc: ['application/msword'],
  docx: [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
  xls: ['application/vnd.ms-excel'],
  xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  ppt: ['application/vnd.ms-powerpoint'],
  pptx: [
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ],
  hwp: ['application/haansofthwp', 'application/x-hwp'],
  hwpx: ['application/haansofthwpx', 'application/vnd.hancom.hwpx'],
  zip: ['application/zip', 'application/x-zip-compressed'],
  txt: ['text/plain'],
  csv: ['text/csv'],
} as const satisfies Record<string, readonly string[]>;

export const ALLOWED_DOCUMENT_MIME_TYPES =
  Object.values(DOCUMENT_MIME_BY_EXT).flat();

export type AllowedDocumentMimeType =
  (typeof ALLOWED_DOCUMENT_MIME_TYPES)[number];

export function isAllowedDocumentType(
  mime: string,
): mime is AllowedDocumentMimeType {
  return (ALLOWED_DOCUMENT_MIME_TYPES as readonly string[]).includes(mime);
}

/** 이미지 또는 문서 — 첨부 업로드 전반에서 허용되는 타입인지 */
export function isAllowedUploadType(mime: string): boolean {
  return isAllowedImageType(mime) || isAllowedDocumentType(mime);
}

/** 확장자 → 표준 MIME. 문서는 DOCUMENT_MIME_BY_EXT 의 첫 값이 표준이다. */
const CANONICAL_MIME_BY_EXT: Readonly<Record<string, string>> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  mp4: 'video/mp4',
  webm: 'video/webm',
  ...Object.fromEntries(
    Object.entries(DOCUMENT_MIME_BY_EXT).map(([ext, mimes]) => [ext, mimes[0]]),
  ),
};

const KNOWN_MIME_TYPES: readonly string[] = [
  ...ALLOWED_IMAGE_MIME_TYPES,
  ...ALLOWED_VIDEO_MIME_TYPES,
  ...ALLOWED_DOCUMENT_MIME_TYPES,
];

/**
 * 업로드에 쓸 MIME. file.type 은 OS 레지스트리 값이라 PC 마다 다르다
 * (한컴오피스가 깔리면 .xlsx → application/haansoftxlsx, .hwp·.csv 는 빈 값 등).
 * 허용 목록에 있으면 그대로, 없으면 확장자로 표준 MIME 을 고른다.
 * presigned 요청·S3 PUT·첨부 메타가 모두 이 값을 써야 서명이 맞는다.
 */
export function resolveUploadMime(file: Pick<File, 'name' | 'type'>): string {
  if (KNOWN_MIME_TYPES.includes(file.type)) return file.type;
  const dot = file.name.lastIndexOf('.');
  const ext = dot >= 0 ? file.name.slice(dot + 1).toLowerCase() : '';
  return CANONICAL_MIME_BY_EXT[ext] ?? file.type;
}

/**
 * 용도별 최대 업로드 크기(bytes). 백엔드 media.constant.ts 와 동기화.
 * 웹에서 먼저 검증해 불필요한 presigned 발급 라운드트립을 방어.
 * (실제 최종 검증·신뢰 경계는 백엔드에서 수행)
 */
const BYTES_PER_KB = 1024;
const KB_PER_MB = 1024;
const MB = BYTES_PER_KB * KB_PER_MB;

const SIZE_MB_XSMALL = 1;
// const SIZE_MB_SMALL = 3;
const SIZE_MB_MEDIUM = 5;
const SIZE_MB_LARGE = 10;
// const SIZE_MB_XLARGE = 15;
// const SIZE_MB_XXLARGE = 20;
const SIZE_MB_VIDEO = 50;
const SIZE_MB_DOCUMENT = 50;

export const MAX_SIZE_BY_PURPOSE: Record<FilePurpose, number> = {
  [FilePurpose.AVATAR]: SIZE_MB_XSMALL * MB,
  [FilePurpose.POST]: SIZE_MB_LARGE * MB,
  [FilePurpose.DOCUMENT]: SIZE_MB_DOCUMENT * MB,
  [FilePurpose.OTHER]: SIZE_MB_MEDIUM * MB,
  [FilePurpose.DOCS_MEDIA]: SIZE_MB_VIDEO * MB,
  [FilePurpose.PUMSEM_MEDIA]: SIZE_MB_LARGE * MB,
  [FilePurpose.PAGE_HEADER]: SIZE_MB_LARGE * MB,
  [FilePurpose.PAGE_HOME]: SIZE_MB_LARGE * MB,
  [FilePurpose.PAGE_STORE]: SIZE_MB_LARGE * MB,
  [FilePurpose.ORG_CERTIFICATE]: SIZE_MB_XSMALL * MB,
};

export function maxSizeForPurpose(purpose: FilePurpose): number {
  return MAX_SIZE_BY_PURPOSE[purpose];
}
